import React from "react";
import {
	Box,
	Button,
	Checkbox,
	FormControl,
	FormControlLabel,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	TextField,
	Typography,
	type SelectChangeEvent,
} from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";

import { Loading } from "@/components/loading";
import type { X5wPrintServiceMachine } from "@/schemas/machine";

export interface X5wPrintServiceDropdownOption {
	readonly key: number;
	readonly text: string;
	readonly value: string;
}

export interface X5wPrintServiceTemplateProps {
	readonly selectedMachine: X5wPrintServiceMachine;
	readonly colorProfiles: X5wPrintServiceDropdownOption[];
	readonly colorModes: X5wPrintServiceDropdownOption[];
	readonly colorants: X5wPrintServiceDropdownOption[];
	readonly technicalJobs: X5wPrintServiceDropdownOption[];
	readonly selectPdfOptions: X5wPrintServiceDropdownOption[];
	readonly imageSource: string | null;
	readonly colorMode: string | null;
	readonly cartonLength: string | null;
	readonly cartonWidth: string | null;
	readonly cartonHeight: string | null;
	readonly loading: boolean;
	readonly isSmartScaling: boolean;
	readonly fssFilepath: string;
	readonly dotFilepath: string;
	readonly shippingFilepath: string;
	readonly barcodeFilepath: string;
	readonly boxId: number;
	readonly onImageSourceChange: (value: string) => void;
	readonly onColorWiseChange: (value: boolean) => void;
	readonly onColorProfileChange: (value: string) => void;
	readonly onColorModeChange: (value: string) => void;
	readonly onColorantChange: (value: string) => void;
	readonly onPackNetSourceChange: (printImageId: string) => void;
	readonly onSftpSourceChange: (value: string) => void;
	readonly onFierySourceChange: (value: string) => void;
	readonly onCartonLengthChange: (value: string) => void;
	readonly onCartonWidthChange: (value: string) => void;
	readonly onCartonHeightChange: (value: string) => void;
	readonly onSmartScalingChange: (value: boolean) => void;
	readonly onFssFilepathChange: (value: string) => void;
	readonly onDotFilepathChange: (value: string) => void;
	readonly onShippingFilepathChange: (value: string) => void;
	readonly onBarcodeFilepathChange: (value: string) => void;
	readonly onBoxIdChange: (value: string) => void;
	readonly onSubmitTestJob: () => void;
}

const imageSourceOptions: X5wPrintServiceDropdownOption[] = [
	{ key: 1, value: "Packnet", text: "Packnet" },
	{ key: 2, value: "SFTP", text: "External SFTP server" },
	{ key: 3, value: "Fiery", text: "Image is located on Fiery server" },
];

const getSelectOptions = (options: X5wPrintServiceDropdownOption[]) =>
	options.map((option) => (
		<MenuItem key={option.key} value={option.value}>
			{option.text}
		</MenuItem>
	));

interface DimensionFieldProps {
	readonly label: string;
	readonly value: string | null;
	readonly testId: string;
	readonly onChange: (value: string) => void;
}

const DimensionField: React.FC<DimensionFieldProps> = ({ label, value, testId, onChange }) => (
	<TextField
		fullWidth
		type="number"
		label={label}
		value={value ?? ""}
		onChange={(event) => onChange(event.target.value)}
		slotProps={{ htmlInput: { "data-testid": testId } }}
	/>
);

export const X5wPrintServiceTemplate: React.FC<X5wPrintServiceTemplateProps> = ({
	selectedMachine,
	colorProfiles,
	colorModes,
	colorants,
	technicalJobs,
	selectPdfOptions,
	imageSource,
	colorMode,
	cartonLength,
	cartonWidth,
	cartonHeight,
	loading,
	isSmartScaling,
	fssFilepath,
	dotFilepath,
	shippingFilepath,
	barcodeFilepath,
	boxId,
	onImageSourceChange,
	onColorWiseChange,
	onColorProfileChange,
	onColorModeChange,
	onColorantChange,
	onPackNetSourceChange,
	onSftpSourceChange,
	onFierySourceChange,
	onCartonLengthChange,
	onCartonWidthChange,
	onCartonHeightChange,
	onSmartScalingChange,
	onFssFilepathChange,
	onDotFilepathChange,
	onShippingFilepathChange,
	onBarcodeFilepathChange,
	onBoxIdChange,
	onSubmitTestJob,
}) => {
	const intl = useIntl();
	const design = selectedMachine.settings.designId;
	const imageSourceDisabled =
		cartonLength === null ||
		cartonLength === "" ||
		cartonWidth === null ||
		cartonWidth === "" ||
		cartonHeight === null ||
		cartonHeight === "";

	return (
		<Stack component="form" spacing={3}>
			<Stack spacing={2}>
				<Typography variant="h3" data-testid="designId">
					<FormattedMessage id="Carton Design {design}" defaultMessage="Carton Design {design}" values={{ design }} />
				</Typography>
				<Typography variant="h3">
					<FormattedMessage id="Enter Carton Dimensions" defaultMessage="Enter Carton Dimensions" />
				</Typography>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
						gap: 2,
					}}
				>
					<DimensionField
						label={intl.formatMessage({ id: "Length", defaultMessage: "Length" })}
						value={cartonLength}
						testId="cartonLength"
						onChange={onCartonLengthChange}
					/>
					<DimensionField
						label={intl.formatMessage({ id: "Width", defaultMessage: "Width" })}
						value={cartonWidth}
						testId="cartonWidth"
						onChange={onCartonWidthChange}
					/>
					<DimensionField
						label={intl.formatMessage({ id: "Height", defaultMessage: "Height" })}
						value={cartonHeight}
						testId="cartonHeight"
						onChange={onCartonHeightChange}
					/>
				</Box>
			</Stack>

			<FormControl fullWidth disabled={imageSourceDisabled} data-testid="ImageSource">
				<InputLabel id="image-source-label">
					<FormattedMessage id="Select Image Source" defaultMessage="Select Image Source" />
				</InputLabel>
				<Select
					labelId="image-source-label"
					value={imageSource ?? ""}
					label={intl.formatMessage({ id: "Select Image Source", defaultMessage: "Select Image Source" })}
					onChange={(event: SelectChangeEvent<string>) => onImageSourceChange(event.target.value)}
				>
					{getSelectOptions(imageSourceOptions)}
				</Select>
			</FormControl>

			{imageSource === "Packnet" && (
				<FormControl fullWidth disabled={selectPdfOptions.length === 0} data-testid="packnet">
					<InputLabel id="packnet-label">
						<FormattedMessage id="Select PDF from list" defaultMessage="Select PDF from list" />
					</InputLabel>
					<Select
						labelId="packnet-label"
						defaultValue=""
						label={intl.formatMessage({ id: "Select PDF from list", defaultMessage: "Select PDF from list" })}
						onChange={(event: SelectChangeEvent<string>) => onPackNetSourceChange(event.target.value)}
					>
						<MenuItem value="">
							{selectPdfOptions.length > 0
								? intl.formatMessage({ id: "Select PDF", defaultMessage: "Select PDF" })
								: intl.formatMessage({ id: "No PDF's Found", defaultMessage: "No PDF's Found" })}
						</MenuItem>
						{getSelectOptions(selectPdfOptions)}
					</Select>
				</FormControl>
			)}

			{imageSource === "SFTP" && (
				<>
					<FormControlLabel
						control={
							<Checkbox checked={isSmartScaling} onChange={(_event, checked) => onSmartScalingChange(checked)} />
						}
						label={<FormattedMessage id="Smart Scaling Job" defaultMessage="Smart Scaling Job" />}
					/>
					{isSmartScaling === false && (
						<TextField
							fullWidth
							label={intl.formatMessage({ id: "Enter SFTP file path", defaultMessage: "Enter SFTP file path" })}
							onChange={(event) => onSftpSourceChange(event.target.value)}
							slotProps={{
								htmlInput: {
									"data-testid": "sftp",
								},
							}}
						/>
					)}
					{isSmartScaling === true && (
						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
								gap: 2,
							}}
						>
							<TextField
								fullWidth
								label={intl.formatMessage({ id: ".fss", defaultMessage: ".fss" })}
								value={fssFilepath}
								onChange={(event) => onFssFilepathChange(event.target.value)}
							/>
							<TextField
								fullWidth
								label={intl.formatMessage({
									id: "Shipping Label Filepath",
									defaultMessage: "Shipping Label Filepath",
								})}
								value={shippingFilepath}
								onChange={(event) => onShippingFilepathChange(event.target.value)}
							/>
							<TextField
								fullWidth
								label={intl.formatMessage({
									id: "DOT Label Filepath",
									defaultMessage: "DOT Label Filepath",
								})}
								value={dotFilepath}
								onChange={(event) => onDotFilepathChange(event.target.value)}
							/>
							<TextField
								fullWidth
								label={intl.formatMessage({
									id: "LPN Label Filepath",
									defaultMessage: "LPN Label Filepath",
								})}
								value={barcodeFilepath}
								onChange={(event) => onBarcodeFilepathChange(event.target.value)}
							/>
							<TextField
								fullWidth
								label={intl.formatMessage({ id: "Box Id", defaultMessage: "Box Id" })}
								type="number"
								value={boxId}
								onChange={(event) => onBoxIdChange(event.target.value)}
							/>
						</Box>
					)}
				</>
			)}

			{imageSource === "Fiery" && (
				<FormControl fullWidth data-testid="fiery">
					<InputLabel id="fiery-label">
						<FormattedMessage id="Select Fiery job" defaultMessage="Select Fiery job" />
					</InputLabel>
					<Select
						labelId="fiery-label"
						defaultValue=""
						label={intl.formatMessage({ id: "Select Fiery job", defaultMessage: "Select Fiery job" })}
						onChange={(event: SelectChangeEvent<string>) => onFierySourceChange(event.target.value)}
					>
						<MenuItem value="">
							<FormattedMessage id="Select" defaultMessage="Select" />
						</MenuItem>
						{getSelectOptions(technicalJobs)}
					</Select>
				</FormControl>
			)}

			<Stack spacing={2}>
				<Typography variant="h3">
					<FormattedMessage id="RIP Settings" defaultMessage="RIP Settings" />
				</Typography>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
						gap: 2,
					}}
				>
					<FormControl fullWidth>
						<InputLabel id="color-wise-label">
							<FormattedMessage id="ColorWise" defaultMessage="ColorWise" />
						</InputLabel>
						<Select
							labelId="color-wise-label"
							defaultValue=""
							label={intl.formatMessage({ id: "ColorWise", defaultMessage: "ColorWise" })}
							onChange={(event: SelectChangeEvent<unknown>) => {
								if (event.target.value === "true" || event.target.value === "false") {
									onColorWiseChange(event.target.value === "true");
								}
							}}
						>
							<MenuItem value="">
								<FormattedMessage id="Select" defaultMessage="Select" />
							</MenuItem>
							<MenuItem value="true">
								<FormattedMessage id="On" defaultMessage="On" />
							</MenuItem>
							<MenuItem value="false">
								<FormattedMessage id="Off" defaultMessage="Off" />
							</MenuItem>
						</Select>
					</FormControl>
					<FormControl fullWidth>
						<InputLabel id="color-profile-label">
							<FormattedMessage id="Color Profile" defaultMessage="Color Profile" />
						</InputLabel>
						<Select
							labelId="color-profile-label"
							defaultValue=""
							label={intl.formatMessage({ id: "Color Profile", defaultMessage: "Color Profile" })}
							onChange={(event: SelectChangeEvent<string>) => onColorProfileChange(event.target.value)}
						>
							<MenuItem value="">
								<FormattedMessage id="Select" defaultMessage="Select" />
							</MenuItem>
							{getSelectOptions(colorProfiles)}
						</Select>
					</FormControl>
					<FormControl fullWidth>
						<InputLabel id="color-mode-label">
							<FormattedMessage id="Color Mode" defaultMessage="Color Mode" />
						</InputLabel>
						<Select
							labelId="color-mode-label"
							value={colorMode ?? ""}
							label={intl.formatMessage({ id: "Color Mode", defaultMessage: "Color Mode" })}
							onChange={(event: SelectChangeEvent<string>) => onColorModeChange(event.target.value)}
							data-testid="ColorMode"
						>
							<MenuItem value="">
								<FormattedMessage id="Select" defaultMessage="Select" />
							</MenuItem>
							{getSelectOptions(colorModes)}
						</Select>
					</FormControl>
					{colorMode === "cmykplus" && (
						<FormControl fullWidth data-testid="Colorant">
							<InputLabel id="colorant-label">
								<FormattedMessage id="Colorant" defaultMessage="Colorant" />
							</InputLabel>
							<Select
								labelId="colorant-label"
								defaultValue=""
								label={intl.formatMessage({ id: "Colorant", defaultMessage: "Colorant" })}
								onChange={(event: SelectChangeEvent<string>) => onColorantChange(event.target.value)}
							>
								<MenuItem value="">
									<FormattedMessage id="Select" defaultMessage="Select" />
								</MenuItem>
								{getSelectOptions(colorants)}
							</Select>
						</FormControl>
					)}
				</Box>
			</Stack>

			<Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
				<Button variant="contained" size="large" onClick={onSubmitTestJob}>
					<FormattedMessage id="Produce Job" defaultMessage="Produce Job" />
				</Button>
				{loading === true && (
					<Loading message={intl.formatMessage({ id: "Sending Job", defaultMessage: "Sending Job" })} />
				)}
			</Stack>
		</Stack>
	);
};
